import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { FirmSignature } from "./FirmSignature";
import { IMAGES } from "./data";

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = ({ onInitiate, started }) => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} id="top" data-testid="hero-section"
      className="relative flex min-h-screen flex-col justify-end overflow-hidden">
      <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
        <img src={IMAGES.skyline} alt="" aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover opacity-40 mix-blend-luminosity" />
        <img src={IMAGES.hero} alt="Ornate international courtroom chamber"
          className="absolute inset-0 h-full w-full object-cover opacity-80" />
      </motion.div>
      <div className="hero-vignette absolute inset-0" />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-14 pt-40 sm:px-6 lg:px-8">
        {started && (
          <>
            <MaskedLine delay={0.35}>
              <span className="mb-6 inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.35em] text-gold">
                <span className="h-px w-10 bg-gold/70" /> {t.hero.overline}
              </span>
            </MaskedLine>

            <h1 className="max-w-5xl font-serif text-4xl font-semibold leading-[1.04] tracking-tight text-slate-50 sm:text-6xl lg:text-7xl">
              <MaskedLine delay={0.45}>{t.hero.l1}</MaskedLine>
              <MaskedLine delay={0.58}>
                <span className="italic text-gold-gradient">{t.hero.l2}</span>
              </MaskedLine>
            </h1>

            <MaskedLine delay={0.75} className="mt-7 max-w-2xl">
              <span className="text-base leading-relaxed text-slate-200 sm:text-lg">{t.hero.sub}</span>
            </MaskedLine>

            <motion.div
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.0, ease: [0.22, 1, 0.36, 1] }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <button onClick={onInitiate} data-testid="hero-primary-cta"
                className="group inline-flex items-center gap-3 bg-gold px-8 py-4 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-navy-deep transition-colors duration-300 hover:bg-gold-hover">
                {t.initiate}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
              </button>
              <a href="#framework" data-testid="hero-secondary-cta"
                className="inline-flex items-center gap-3 border border-slate-400/40 px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-slate-200 transition-colors duration-300 hover:border-gold hover:text-gold">
                {t.hero.secondary}
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 1.15, ease: [0.22, 1, 0.36, 1] }}
              className="mt-12"
            >
              <FirmSignature size="text-lg sm:text-xl lg:text-2xl" testid="hero-firm-signature" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              transition={{ duration: 1.2, delay: 1.3 }}
              className="mt-12 grid grid-cols-2 gap-px border border-navy-border/80 bg-navy-border/60 lg:grid-cols-4"
              data-testid="hero-stats"
            >
              {t.hero.stats.map((s) => (
                <div key={s.label} className="bg-navy-deep/70 p-5 backdrop-blur-sm lg:p-6">
                  <div className="font-serif text-3xl font-semibold text-gold lg:text-4xl">{s.value}</div>
                  <div className="mt-2 text-[13px] font-semibold tracking-wide text-slate-200">{s.label}</div>
                  <div className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">{s.sub}</div>
                </div>
              ))}
            </motion.div>
          </>
        )}
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: started ? 1 : 0 }} transition={{ delay: 1.8, duration: 1 }}
        className="pointer-events-none absolute bottom-5 left-1/2 z-10 hidden -translate-x-1/2 lg:block"
      >
        <ChevronDown className="h-5 w-5 animate-bounce text-gold/70" />
      </motion.div>
    </section>
  );
};
